'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '../../toast/ToastProvider';
import { normalizeDocumentationUrls } from '../utils';
import {
  areDocumentationUrlInputsValid,
  canAddDocumentationUrlInput,
  removeDocumentationUrlInputAt,
  replaceDocumentationUrlAt,
  toDocumentationUrlDialogInputs,
} from './documentationUrls.utils';
import type { InitialDocumentationActionResolver } from './useInitialDocumentationAction';
import type { PersistDocumentationUrls } from './usePersistDocumentationUrls';
import type { StagedDocumentationState } from './useStagedDocumentation';

export type DocumentationLinksDialogState = {
  isOpen: boolean;
  urls: string[];
  canAddLink: boolean;
  canConfirm: boolean;
  isSaving: boolean;
  open: () => void;
  dismiss: () => void;
  changeUrl: ({ index, value }: { index: number; value: string }) => void;
  addUrl: () => void;
  removeUrl: ({ index }: { index: number }) => void;
  save: () => Promise<void>;
};

export const useDocumentationLinksDialog = ({
  visibleDocumentationUrls,
  isDeferred,
  showsSubmittingState,
  persistDocumentationUrls,
  stageDocumentationUrls,
  initialAction,
}: {
  visibleDocumentationUrls: string[];
  isDeferred: boolean;
  showsSubmittingState: boolean;
  persistDocumentationUrls: PersistDocumentationUrls;
  stageDocumentationUrls: StagedDocumentationState['stageDocumentationUrls'];
  initialAction: InitialDocumentationActionResolver;
}): DocumentationLinksDialogState => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [urls, setUrls] = useState<string[]>([]);
  const { resolveAdvance, resolveCancel, beginAdvance, endAdvance } =
    initialAction;

  const dismiss = useCallback(() => {
    setIsOpen(false);
    setUrls([]);
    resolveCancel();
    endAdvance();
  }, [endAdvance, resolveCancel]);

  const open = useCallback(() => {
    setUrls(toDocumentationUrlDialogInputs(visibleDocumentationUrls));
    setIsOpen(true);
  }, [visibleDocumentationUrls]);

  const changeUrl = useCallback(
    ({ index, value }: { index: number; value: string }) => {
      setUrls((previousUrls) =>
        replaceDocumentationUrlAt({ urls: previousUrls, index, value }),
      );
    },
    [],
  );

  const addUrl = useCallback(() => {
    setUrls((previousUrls) => [...previousUrls, '']);
  }, []);

  const removeUrl = useCallback(({ index }: { index: number }) => {
    setUrls((previousUrls) =>
      removeDocumentationUrlInputAt({ urls: previousUrls, index }),
    );
  }, []);

  const save = useCallback(async () => {
    const normalized = normalizeDocumentationUrls(urls);

    if (showsSubmittingState) {
      setIsSaving(true);
    }

    try {
      if (isDeferred) {
        beginAdvance();
        stageDocumentationUrls(normalized);
        dismiss();
        resolveAdvance();

        return;
      }

      const didSave = await persistDocumentationUrls({ urls: normalized });

      if (didSave) {
        beginAdvance();
        dismiss();
        resolveAdvance();
        toasterApi.success({ description: t('linkAddedSuccessfully') });
      }
    } finally {
      if (showsSubmittingState) {
        setIsSaving(false);
      }
    }
  }, [
    beginAdvance,
    dismiss,
    isDeferred,
    persistDocumentationUrls,
    resolveAdvance,
    showsSubmittingState,
    stageDocumentationUrls,
    t,
    urls,
  ]);

  return {
    isOpen,
    urls,
    canAddLink: canAddDocumentationUrlInput(urls),
    canConfirm: areDocumentationUrlInputsValid(urls),
    isSaving,
    open,
    dismiss,
    changeUrl,
    addUrl,
    removeUrl,
    save,
  };
};
